import { FC, ChangeEvent, useRef, useEffect, KeyboardEvent } from 'react'

import styled from 'styled-components'

export interface PrimarySliderProps {
  min: number
  max: number
  value: number
  onChange: (value: number) => void
  step?: number
  className?: string
  formatValue?: (value: number) => string
  onChangeComplete?: (value: number) => void
  heightMode?: boolean
  isViewsBasedModel?: boolean
  warningThreshold?: number
  disabled?: boolean
}

const Container = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  row-gap: 8px;
  min-width: 80px;
  user-select: none;
  word-break: break-all;
  align-self: stretch;
  min-height: 0;
  touch-action: none;
`

const Label = styled.div`
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  width: 100%;
  flex-shrink: 0;
`

const SliderWrapper = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  min-height: 0;
  height: 100%;
`

const StyledInput = styled.input<{
  percentage: number
  $heightMode?: boolean
  $warningPercentage?: number
}>`
  appearance: none;
  writing-mode: vertical-lr;
  direction: rtl;
  width: 6px;
  height: ${({ $heightMode }) => ($heightMode ? '82px' : 'auto')};
  border-radius: 16px;
  outline: none;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:disabled::-webkit-slider-thumb {
    cursor: not-allowed;
  }

  &:disabled::-moz-range-thumb {
    cursor: not-allowed;
  }

  background: ${({ theme, percentage, $warningPercentage }) => {
    const warningColor = theme.color.mediumOrange
    const successColor = theme.color.brandGreen
    const trackColor = theme.color.lightGrey

    if (typeof $warningPercentage === 'number' && $warningPercentage > 0) {
      if (percentage <= $warningPercentage) {
        return `linear-gradient(
          to top,
          ${warningColor} 0%,
          ${warningColor} ${percentage}%,
          ${trackColor} ${percentage}%,
          ${trackColor} 100%
        )`
      }

      return `linear-gradient(
        to top,
        ${successColor} 0%,
        ${successColor} ${percentage}%,
        ${trackColor} ${percentage}%,
        ${trackColor} 100%
      )`
    }

    return `linear-gradient(
      to top,
      ${successColor} 0%,
      ${successColor} ${percentage}%,
      ${trackColor} ${percentage}%,
      ${trackColor} 100%
    )`
  }};

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    background-color: ${({ theme }) => theme.unchangingColor.white};
    border: 2px solid ${({ theme }) => theme.color.lightGrey};
    border-radius: 50%;
    cursor: grab;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::-webkit-slider-thumb:active {
    cursor: grabbing;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background-color: ${({ theme }) => theme.unchangingColor.white};
    border: 2px solid ${({ theme }) => theme.color.mediumGrey};
    border-radius: 50%;
    cursor: grab;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::-webkit-slider-runnable-track {
    height: 100%;
  }

  &::-moz-range-track {
    height: 100%;
  }
`

export const PrimarySlider: FC<PrimarySliderProps> = ({
  min,
  max,
  value,
  onChange,
  step = 1,
  className,
  formatValue,
  onChangeComplete,
  heightMode,
  isViewsBasedModel,
  warningThreshold,
  disabled,
}) => {
  const isSingleValueRange = max <= min

  const sliderMin = isSingleValueRange ? 0 : min
  const sliderMax = isSingleValueRange ? 1 : max
  const sliderStep = isSingleValueRange ? 1 : step

  const clampToStep = (val: number): number => {
    const steps = Math.round((val - sliderMin) / sliderStep)
    return sliderMin + steps * sliderStep
  }

  const sliderValue = isSingleValueRange ? 1 : clampToStep(value)

  const valueRange = Math.max(sliderMax - sliderMin, 1)

  const PERCENTAGE_DECIMAL_PRECISION = 4
  const percentageRaw = ((sliderValue - sliderMin) / valueRange) * 100

  const percentage = Math.min(
    Math.max(Number(percentageRaw.toFixed(PERCENTAGE_DECIMAL_PRECISION)), 0),
    100,
  )
  const warningPercentage =
    typeof warningThreshold === 'number' && isViewsBasedModel
      ? Math.min(
          Math.max(
            Number(
              (((warningThreshold - sliderMin) / valueRange) * 100).toFixed(
                PERCENTAGE_DECIMAL_PRECISION,
              ),
            ),
            0,
          ),
          100,
        )
      : 0

  const lastValueRef = useRef(sliderValue)

  useEffect(() => {
    lastValueRef.current = sliderValue
  }, [sliderValue])

  const formatDisplayValue = (val: number): string =>
    formatValue ? formatValue(val) : val.toString()

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const raw = Number(e.target.value)
    const logicalValue = isSingleValueRange ? max : clampToStep(raw)

    onChange(logicalValue)
    lastValueRef.current = logicalValue
  }

  const commit = (): void => {
    if (onChangeComplete) {
      onChangeComplete(lastValueRef.current)
    }
  }

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      commit()
    }
  }

  return (
    <Container className={className}>
      <Label>{formatDisplayValue(max)}</Label>
      <SliderWrapper>
        <StyledInput
          type='range'
          min={sliderMin}
          max={sliderMax}
          value={sliderValue}
          step={sliderStep}
          onChange={handleChange}
          onPointerUp={commit}
          onBlur={commit}
          onKeyUp={handleKeyUp}
          percentage={percentage}
          aria-label='Primary slider'
          $heightMode={heightMode}
          $warningPercentage={warningPercentage}
          disabled={disabled}
        />
      </SliderWrapper>
      <Label>{formatDisplayValue(min)}</Label>
    </Container>
  )
}
